<script lang="ts">
	import { locateID, locate } from "@xbs/lib-dom";
	import { isSameId, portal } from "@xbs/lib-kanban";
	import type { ICoords } from "@xbs/lib-kanban";
	import { uid } from "@xbs/lib-state";
	import { createEventDispatcher, getContext } from "svelte";

	import Icon from "./Icon.svelte";

	export let data: any[];
	export let coords: { x: number; y: number };
	export let id = uid();
	export let cancel = null;
	export let container = document.body;

	export let parentId = null;
	export let node: HTMLElement = null;
	export let dispatch = createEventDispatcher();

	const theme = getContext("wx-theme") || "material";

	$: {
		if (node) {
			coords = getCorrectedCoords(node, coords);
		}
	}

	$: parentNode = null;

	let subMenu = {
		id: null,
		coords: { x: null, y: null },
		node: null,
	};

	function getCorrectedCoords(node: HTMLElement, coords: ICoords) {
		const bodyRect = document.body.getBoundingClientRect();
		const menuRect = node.getBoundingClientRect();

		const { offsetLeft, offsetTop } = node;

		let y = coords.y;
		let x = coords.x;

		if (menuRect.bottom >= bodyRect.bottom) {
			y = offsetTop - menuRect.height;
		}
		if (menuRect.right >= bodyRect.right) {
			x = offsetLeft - menuRect.width;
		}

		return { x, y };
	}

	function handleItemOver(e: any) {
		const itemId = locateID(e, "data-menu-item-id");

		const dParentId = locateID(e, "data-menu-parent-id");

		const item = data.find(i => isSameId(i.id, itemId));

		if (item?.items?.length) {
			parentNode = locate(e, "data-menu-item-id");

			const parentRect = parentNode.getBoundingClientRect();
			let x = node.offsetLeft + parentRect.width;
			let y = node.offsetTop + parentNode.offsetTop;

			subMenu.coords.x = x;
			subMenu.coords.y = y;

			subMenu.id = itemId;
		} else if (!dParentId || isSameId(parentId, dParentId)) {
			subMenu = {
				id: null,
				coords: { x: null, y: null },
				node: null,
			};
		}
	}

	function handleMenuClick(e: any) {
		const itemId = locateID(e, "data-menu-item-id");
		const parentId = locateID(e, "data-menu-parent-id");

		if (itemId) {
			dispatch("click", {
				id: itemId,
				item: data.find(item => isSameId(item.id, itemId)),
				parent: parentId,
			});
		}
	}

	function down(e: any) {
		const parentId = locateID(e, "data-menu-parent-id");

		if (!node.contains(e.target) && !parentId) {
			if (cancel) cancel(e);
		}
	}

</script>

<svelte:window
	on:mousedown={down}
	on:pointerover|stopPropagation={handleItemOver} />

<div
	style="left: {coords.x}px; top: {coords.y}px;"
	class="wx-popup wx-{theme}-theme"
	class:wx-menu-submenu-item={parentId}
	bind:this={node}
	use:portal={{ container }}>
	<ul
		on:click|stopPropagation={handleMenuClick}
		data-menu-id={id}
		class="wx-menu">
		{#each data as item}
			{#if item.type === 'separator'}
				<div class="wx-separator" style="padding: 6px 16px;">
					<div class="wx-line" />
				</div>
			{:else}
				<li
					class="wx-menu-item"
					class:wx-disabled={item.disabled}
					data-menu-item-id={item.id}
					data-menu-parent-id={parentId}
					class:wx-menu-submenu-item={parentId}>
					<div class="wx-menu-main-content">
						<Icon name={item.icon} size={20} />
						<span class="wx-menu-description">{item.label}</span>
					</div>
					{#if item.items?.length}
						<Icon name="wxi-menu-right" size={20} />
					{:else if item.hotkey}
						<span class="wx-menu-hotkey">{item.hotkey} </span>
					{/if}
					{#if isSameId(subMenu.id, item.id)}
						<svelte:self
							parentId={item.id}
							data={item.items}
							coords={subMenu.coords}
							bind:node={subMenu.node}
							{container}
							{dispatch} />
					{/if}
				</li>
			{/if}
		{/each}
	</ul>
</div>

<style>
	.wx-popup {
		position: absolute;
		z-index: 9999;
		box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
	}

	.wx-menu {
		padding: 0;
		margin: 0;
		overflow: auto;

		background: var(--wx-background);
		font-family: var(--wx-font-family);
		font-size: var(--wx-font-size);
		line-height: var(--wx-line-height);
	}
	.wx-menu-item {
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: space-between;

		padding: var(--wx-padding);
		min-width: var(--wx-kanban-menu-min-width);
	}
	.wx-disabled {
		opacity: 0.5;
		pointer-events: none;
	}
	.wx-menu-item:hover {
		background: var(--wx-background-hover);
	}
	.wx-menu-submenu-item {
		justify-content: flex-start;
	}
	.wx-menu-main-content {
		display: flex;
		align-items: center;
	}
	.wx-menu-description {
		color: var(--wx-kanban-menu-color);

		padding: 0 calc(var(--wx-padding) / 2);
		padding-left: var(--wx-padding);
	}
	.wx-menu-hotkey {
		color: var(--wx-color-secondary-light);
	}

	.wx-separator {
		display: flex;
		flex-direction: row;
		align-items: center;
	}
	.wx-line {
		border-bottom: 1px solid var(--wx-border-color);
		flex: 1 1 auto;
	}

</style>
